import { useMemo } from 'react';
import { Authorize, Table } from '@/components';
import { BUSINESS_ROLE_GROUPS } from '@/components/security/AppRoles';
import { IconButton } from '@mui/material';
import {
  Edit as EditIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useLogisticOperatorsPagination } from '../hooks';
import { logisticOperatorsTableColumns } from '../config';
import useLogisticOperators from '../hooks/useLogisticOperators';
import { LogisticOperatorsFilter } from '../models';

const columns = logisticOperatorsTableColumns;

type Props = { filter: LogisticOperatorsFilter };

const LogisticOperatorsTable = ({ filter }: Props) => {
  const navigate = useNavigate();
  const { currentPage, pageSize, handlePageChange, handlePageSizeChange } =
    useLogisticOperatorsPagination();

  const {
    logisticOperators,
    totalItems,
    totalPages,
    isLoading,
    isContradictoryFilter,
  } = useLogisticOperators({
    filters: filter,
    page: currentPage,
    pageSize,
  });

  const renderActions = (rowData: Record<string, any>) => (
    <div className='flex gap-1'>
      <IconButton
        onClick={() =>
          navigate(`/business/logistic-operator/${rowData.id}/dashboard`)
        }
        size='small'
      >
        <VisibilityIcon fontSize='small' />
      </IconButton>
      <Authorize permission={BUSINESS_ROLE_GROUPS.management} fallback={null}>
        <IconButton
          onClick={() =>
            navigate(`/business/logistic-operator/${rowData.id}/edit`)
          }
          size='small'
        >
          <EditIcon fontSize='small' />
        </IconButton>
      </Authorize>
    </div>
  );

  const memoColumns = useMemo(() => columns, []);

  if (isContradictoryFilter) {
    return (
      <div className='w-full p-5 flex flex-col bg-white rounded-lg shadow-md gap-5'>
        <div className='text-center text-amber-800 py-8'>
          Hay dos operadores distintos seleccionados (nombre y razón social).
          Limpiá uno de los campos o elegí el mismo operador en ambos y volvé a
          buscar.
        </div>
      </div>
    );
  }

  return (
    <div className='w-full p-5 flex flex-col bg-white rounded-lg shadow-md gap-5'>
      {isLoading || logisticOperators.length > 0 ? (
        <Table
          data={logisticOperators}
          columns={memoColumns}
          renderActions={renderActions}
          isLoading={isLoading}
          pagination={{
            totalItems,
            totalPages,
            currentPage,
            rowsPerPage: pageSize,
            onPageChange: handlePageChange,
            onRowsPerPageChange: handlePageSizeChange,
          }}
        />
      ) : (
        <div className='text-center text-gray-500 py-8'>
          No hay operadores logísticos por el momento
        </div>
      )}
    </div>
  );
};

export default LogisticOperatorsTable;
